import React from 'react';
import { FileX, FileText, FileSpreadsheet, ArrowRight, IndianRupee } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ResizableSplitPane } from '../components/ResizableSplitPane';
import { BudgetSearchFilter } from '../components/orderingtSearchFilter';
import { PdfViewer } from '../components/pdfviewer';
import { XlViewer } from '../components/xl_viewer';
import { InitialQuotationMaker } from '../components/ordering_quotation_maker';
import { QuotationPreviewModal } from '../components/ordering_quotation_preview';
import { API_BASE_URL } from '../../../services/api';
import { format } from 'date-fns';

export const QuotationPrepScreen = ({
  searchQuery,
  setSearchQuery,
  filterType,
  setFilterType,
  loadingDocs,
  catalogDocs,
  stockSelections,
  setStockSelections,
  selectedDocument,
  setSelectedDocument,
  quotationDetails,
  setQuotationDetails,
  quotationTableData,
  setQuotationTableData,
  customTableTotal,
  setCustomTableTotal,
  showPreview,
  setShowPreview,
  handleSaveQuotation,
  handleCancelQuotation
}) => {
  const totalBudget = customTableTotal;

  return (
    <ResizableSplitPane
      initialLeftWidth={30}
      leftContent={
        <div className="flex flex-col h-full min-h-[400px]">
          <BudgetSearchFilter
            searchTerm={searchQuery}
            onSearchChange={setSearchQuery}
            filterType={filterType}
            onFilterChange={setFilterType}
          />
          <div className={`flex-1 border border-slate-200 rounded-lg bg-white overflow-hidden shadow-sm flex flex-col ${filterType !== 'Stock' && (loadingDocs || (catalogDocs.length === 0 && !selectedDocument)) ? 'items-center justify-center text-center p-8' : ''}`}>
            {loadingDocs ? (
              <div className="flex items-center justify-center p-8 text-slate-500">Loading {filterType === 'Stock' ? 'purchases' : 'catalog files'}...</div>
            ) : filterType === 'Stock' ? (
              catalogDocs.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                    <FileX className="text-slate-300" size={32} />
                  </div>
                  <h3 className="text-lg font-medium text-slate-800">No purchases found</h3>
                </div>
              ) : (() => {
                const flatItems = catalogDocs.flatMap(inv => {
                  const vendor =
                    (typeof inv.vendor === 'object' ? (inv.vendor?.vendorName || inv.vendor?.name) : inv.vendor) ||
                    inv.vendorName || 'Unknown Vendor';
                  const invoiceNo = inv.invoiceNo || inv.invoiceNumber || '';
                  const date = (inv.invoiceDate || inv.createdAt)
                    ? new Date(inv.invoiceDate || inv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '';

                  if (inv.items && inv.items.length > 0) {
                    return inv.items.map((it, idx) => ({
                      _key: `${inv._id}-${idx}`,
                      inv,
                      itemName: it.item?.itemName || it.item?.name || it.description || '—',
                      vendor,
                      invoiceNo,
                      date,
                      status: inv.status || 'Completed',
                      availableQty: Number(it.availableQty ?? it.quantity ?? 0),
                      purchasedQty: Number(it.quantity ?? 0),
                      unit: it.item?.units?.unitName || it.item?.unit?.unitName || '',
                      purchaseRate: Number(it.purchaseRate || 0),
                      amount: Number(it.netBill || it.baseAmount || 0)
                    }));
                  }
                  return [{
                    _key: inv._id,
                    inv,
                    itemName: inv.item?.itemName || inv.item?.name || inv.description || '—',
                    vendor,
                    invoiceNo,
                    date,
                    status: inv.status || 'Completed',
                    availableQty: Number(inv.quantity ?? 0),
                    purchasedQty: Number(inv.quantity ?? 0),
                    unit: inv.item?.units?.unitName || '',
                    purchaseRate: 0,
                    amount: Number(inv.finalNetPayment || inv.netBill || 0)
                  }];
                }).filter(item => item.status !== 'Queue' && !item.inv?.isQueue);

                return (
                  <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                      {flatItems.map((flatItem) => {
                        const isSelected = !!stockSelections[flatItem._key];
                        const currentQty = stockSelections[flatItem._key]?.qty ?? 1;
                        const isOverStock = isSelected && flatItem.availableQty > 0 && Number(currentQty) > flatItem.availableQty;

                        return (
                          <div
                            key={flatItem._key}
                            onClick={() => {
                              setStockSelections(prev => {
                                const next = { ...prev };
                                if (next[flatItem._key]) { delete next[flatItem._key]; }
                                else { next[flatItem._key] = { flatItem, qty: 1 }; }
                                return next;
                              });
                            }}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all select-none ${isSelected ? 'border-l-2' : 'hover:bg-slate-50 border-l-2 border-transparent'}`}
                            style={isSelected ? { backgroundColor: '#eff6ff', borderLeftColor: '#2563eb' } : {}}
                          >
                            <div
                              className="w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-all duration-150"
                              style={isSelected ? { backgroundColor: '#2563eb', borderColor: '#2563eb' } : { backgroundColor: 'white', borderColor: '#cbd5e1' }}
                            >
                              {isSelected && (
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className={`text-[13px] font-semibold truncate ${isSelected ? 'text-brand-blue' : 'text-slate-800'}`} title={flatItem.itemName}>
                                {flatItem.itemName}
                              </span>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                                <span className="text-[11px] font-medium text-slate-500 truncate">{flatItem.vendor}</span>
                                {flatItem.status === 'Queue' && (
                                  <><span className="text-slate-300 text-[10px]">•</span><span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">Queue</span></>
                                )}
                                {flatItem.invoiceNo && <><span className="text-slate-300 text-[10px]">•</span><span className="text-[10px] font-bold text-brand-blue bg-blue-50 px-1.5 py-0.5 rounded">{flatItem.invoiceNo}</span></>}
                                {flatItem.date && <><span className="text-slate-300 text-[10px]">•</span><span className="text-[11px] text-slate-400">{flatItem.date}</span></>}
                                {flatItem.availableQty > 0 && (
                                  <><span className="text-slate-300 text-[10px]">•</span>
                                    <span className={`text-[11px] font-bold ${isOverStock ? 'text-red-500' : 'text-emerald-600'}`}>
                                      {flatItem.availableQty} {flatItem.unit} Stock
                                    </span></>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0" onClick={e => e.stopPropagation()}>
                              {flatItem.purchaseRate > 0 && (
                                <div className="flex items-center gap-0.5 text-[12px] font-bold text-slate-600">
                                  <IndianRupee size={11} className="text-slate-400" />
                                  <span>{Number(flatItem.purchaseRate).toLocaleString('en-IN')}</span>
                                  <span className="text-[10px] text-slate-400 font-normal">/pc</span>
                                </div>
                              )}
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-wide">Qty</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={isSelected ? currentQty : ''}
                                  placeholder="0"
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    if (val <= 0) {
                                      setStockSelections(prev => {
                                        const next = { ...prev };
                                        delete next[flatItem._key];
                                        return next;
                                      });
                                    } else {
                                      setStockSelections(prev => ({
                                        ...prev,
                                        [flatItem._key]: { ...(prev[flatItem._key] || { flatItem }), qty: val }
                                      }));
                                    }
                                  }}
                                  className="w-14 text-center px-1 py-1 text-[12px] font-bold border rounded-lg outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:ring-2"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : selectedDocument ? (
              selectedDocument.fileType?.includes('pdf') ? (
                <PdfViewer
                  selectedDocument={selectedDocument}
                  onClose={() => setSelectedDocument(null)}
                />
              ) : (
                <XlViewer
                  selectedDocument={selectedDocument}
                  onClose={() => setSelectedDocument(null)}
                />
              )
            ) : catalogDocs.length === 0 ? (
              <>
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                  <FileX className="text-slate-300" size={32} />
                </div>
                <h3 className="text-lg font-medium text-slate-800">No documents found</h3>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto w-full divide-y divide-slate-100">
                {catalogDocs.map(doc => {
                  const isPdf = doc.fileType?.includes('pdf');
                  const isExcel = doc.fileType?.includes('spreadsheet') || doc.fileType?.includes('excel') || doc.fileType?.includes('csv') || doc.fileName?.endsWith('.xlsx') || doc.fileName?.endsWith('.xls');

                  return (
                    <div
                      key={doc._id}
                      onClick={() => {
                        if (isPdf || isExcel) {
                          setSelectedDocument(doc);
                        } else if (doc.fileUrl) {
                          const token = localStorage.getItem('blum_token');
                          const relativeUrl = doc.fileUrl.startsWith('http') ? doc.fileUrl.replace(/^.*\/\/[^\/]+/, '') : doc.fileUrl;
                          const apiPath = relativeUrl.startsWith('/api') ? relativeUrl : `/api${relativeUrl}`;
                          window.open(`${API_BASE_URL}${apiPath}?token=${token}`, '_blank');
                        }
                      }}
                      className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isPdf ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                          {isPdf ? <FileText size={20} /> : <FileSpreadsheet size={20} />}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-slate-800 truncate">{doc.title}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500 truncate">{doc.category || 'Uncategorized'}</span>
                            <span className="text-xs text-slate-300">•</span>
                            <span className="text-xs text-slate-400 whitespace-nowrap">{format(new Date(doc.createdAt), 'MMM dd, yyyy')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      }
      rightContent={({ toggleFullScreen, isFullScreen }) => (
        <div className="flex flex-col min-h-[400px] h-full">
          <div className="flex flex-col gap-3 flex-1 overflow-y-auto pr-2">
            <InitialQuotationMaker
              onTotalChange={setCustomTableTotal}
              onDataChange={setQuotationTableData}
              details={quotationDetails}
              setDetails={setQuotationDetails}
              toggleFullScreen={toggleFullScreen}
              isFullScreen={isFullScreen}
              stockSelections={stockSelections}
            />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200">
            {/* Estimated Total commented out as requested */}
            {/* <div className="flex justify-between items-end mb-6">
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Estimated Total</span>
              <span className="text-3xl font-bold text-brand-blue flex items-center leading-none">
                <IndianRupee size={24} className="mr-1" />
                {totalBudget.toLocaleString('en-IN')}
              </span>
            </div> */}
            <div className="flex flex-col xl:flex-row justify-end gap-3 transition-all pb-2">
              <Button variant="secondary" className="w-full xl:w-auto" onClick={handleCancelQuotation}>
                Cancel
              </Button>
              <Button
                variant="orange"
                className="w-full xl:w-auto"
                onClick={() => setShowPreview(true)}
                rightIcon={<ArrowRight size={16} />}
              >
                Preview
              </Button>
            </div>
          </div>
          {showPreview && (
            <QuotationPreviewModal
              onClose={() => setShowPreview(false)}
              onSubmit={handleSaveQuotation}
              data={{
                projectName: quotationDetails.projectName || "-",
                clientName: quotationDetails.projectName || "-",
                date: quotationDetails.date || "-",
                site: quotationDetails.site || "-",
                quotationNo: quotationDetails.quotationNo || "-",
                contactRef: quotationDetails.contactRef || "-",
                designer: quotationDetails.referenceDesigner || "-",
                rooms: [
                  {
                    roomName: "Quotation Prep",
                    items: quotationTableData.rows.map(r => {
                      if (r.type === 'horizontal') {
                        return { isHorizontal: true, name: r.text || r.particulars || "" };
                      }
                      const parts = (r.particulars || "").split('\n');
                      const name = parts[0] || "";
                      const details = parts.slice(1);
                      const qty = Number(r.quantity) || 1;
                      const amt = Number(r.amount) || 0;
                      return {
                        name: name,
                        image: r.image || "",
                        description: r.description || "-",
                        quantity: r.quantity || 1,
                        amount: r.amount ? `₹${(qty * amt).toLocaleString('en-IN')}` : "-",
                        details: details
                      };
                    }).filter(Boolean)
                  }
                ],
                totals: {
                  total: `₹${quotationTableData.subTotal.toLocaleString('en-IN')}`,
                  discount: `₹${quotationTableData.discountAmount.toLocaleString('en-IN')}`,
                  gst: `₹${quotationTableData.gstAmount.toLocaleString('en-IN')}`,
                  grandTotal: `₹${quotationTableData.grandTotal.toLocaleString('en-IN')}`
                }
              }}
            />
          )}
        </div>
      )}
    />
  );
};
